import { useEffect, useState } from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { getEnv } from "@accredo/fe-shared/env.js";
import { Button } from "@accredo/fe-shared/theme/components";
import { colorsFoundation } from "@accredo/fe-shared/theme/tokens/colors-foundation.js";

import { Card, DataRow } from "./DetailCard.jsx";

const EMPTY = "–";
const VISIBLE_CLAIMS = 8;

// A count: label and value on one line, as in the click dummy.
function CountRow({ label, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * The Schadenzuordnung card of the Vertrag detail page. Only rendered for a
 * user who may use the Schadenportal; the numbers link into it.
 */
export default function ContractClaimsCard({ vertragId }) {
  const [summary, setSummary] = useState(null);
  const [showAllClaims, setShowAllClaims] = useState(false);
  const claims_url = (getEnv("VITE_SCHADENPORTAL_URL") ?? "").replace(
    /\/$/,
    ""
  );

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setShowAllClaims(false);

    backendGet(`/portal/vertrags/${vertragId}/claims-summary`, {
      label: "Schäden",
    }).then((result) => {
      if (!cancelled && result) {
        setSummary(result.data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [vertragId]);

  const claims = summary?.claims ?? [];
  const hiddenCount = claims.length - VISIBLE_CLAIMS;
  const shownClaims = showAllClaims ? claims : claims.slice(0, VISIBLE_CLAIMS);

  return (
    <Card title="Schadenzuordnung">
      <Stack spacing={2} sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
        <CountRow label="Abgewickelte Schäden">
          {summary?.settled ?? EMPTY}
        </CountRow>
        <CountRow label="Aktive Schäden">{summary?.active ?? EMPTY}</CountRow>
        <DataRow label="Schadennummern">
          {claims.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5 }}>
              {shownClaims.map((claim) => (
                <Link
                  key={claim.id}
                  href={`${claims_url}/schaeden/${claim.id}`}
                >
                  {claim.schaden_nummer_accredo}
                </Link>
              ))}
              {hiddenCount > 0 && (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setShowAllClaims((shown) => !shown)}
                  sx={{ fontWeight: 400, color: colorsFoundation.neutral[700] }}
                >
                  {showAllClaims
                    ? "weniger anzeigen"
                    : `und ${hiddenCount} weitere`}
                </Link>
              )}
            </Box>
          ) : (
            EMPTY
          )}
        </DataRow>
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
          <Button
            variant="tertiary"
            href={`${claims_url}/schaeden?scope=all`}
            endIcon={<ChevronRightOutlined />}
          >
            Zum Schadenportal
          </Button>
        </Box>
      </Stack>
    </Card>
  );
}
